import { Graphics } from "pixi.js"
import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { drawnFloorRects } from "./floor_art"

export type OpeningEdgeSide = "north" | "east" | "south" | "west"
export interface OpeningEdge { x: number; y: number; side: OpeningEdgeSide }

type OpeningMap = Pick<TileMapMetaProvider, "twoStoreyHouse" | "resolveTileMapFromLayerId" |
    "isFloorOpeningAt" | "isHiddenTile" | "isStairAt">

/** Whether a cell shows floor art an opening can have a rim against (not another opening, not a stair). */
const hasFloorArt = (provider: OpeningMap, x: number, y: number): boolean => {
    const tile = provider.resolveTileMapFromLayerId("map2")?.[x]?.[y]
    return !!tile && tile !== "0" && !provider.isHiddenTile(tile) && !provider.isFloorOpeningAt(x, y) && !provider.isStairAt(x, y)
}

/** Only edges shared by an explicit opening and visible floor art need a rim.
 * Adjacent openings contribute no shared edge; outside space and stairs aren't holes. */
export function floorOpeningEdges(provider: OpeningMap): OpeningEdge[] {
    if (!(provider.twoStoreyHouse?.floor > 0)) return []
    const markers = provider.resolveTileMapFromLayerId("sprite2")
    const edges: OpeningEdge[] = []
    const neighbors: [OpeningEdgeSide, number, number][] = [
        ["north", 0, -1], ["east", 1, 0], ["south", 0, 1], ["west", -1, 0],
    ]
    markers?.forEach((column, x) => column?.forEach((marker, y) => {
        if (!marker || marker === "0" || !provider.isFloorOpeningAt(x, y)) return
        for (const [side, dx, dy] of neighbors) {
            if (hasFloorArt(provider, x + dx, y + dy)) edges.push({ x, y, side })
        }
    }))
    return edges
}

/** Depth cues inherit their floor's projection and stay inside the surviving floor artwork. */
export class FloorOpeningEdges extends Graphics {
    private provider?: TileMapMetaProvider
    private version?: string
    private floorAt: (x: number, y: number) => boolean = () => false

    constructor() {
        super()
        this.interactive = false
        // Tilemaps draw at zero, stairs and bodies above this ground decoration.
        this.zIndex = 0.5
    }

    sync(provider?: TileMapMetaProvider): void {
        const version = provider && `${provider.getPresentationVersion()}:${provider.twoStoreyHouse?.floor}`
        if (this.provider === provider && this.version === version) return
        this.provider = provider
        this.version = version
        this.clear()
        const edges = provider ? floorOpeningEdges(provider) : []
        this.visible = edges.length > 0
        this.floorAt = (x, y) => !!provider && hasFloorArt(provider, x, y)
        // Two bands of ambient shade, independent of directional building shadows.
        for (const edge of edges) {
            this.band(edge, edge.side === "north" ? 7 : 4, 0x171410, 0.12)
            this.band(edge, edge.side === "north" ? 5 : 2, 0x171410, 0.18)
        }
        // Looking down exposes the face of the floor along the far (north) edge.
        for (const edge of edges) {
            if (edge.side === "north") this.band(edge, 3, 0x4a4037, 0.95)
        }
        for (const edge of edges) this.band(edge, 1, 0x171410, 0.88)
    }

    /** Shade the floor face next to the opening, never the empty opening or a transparent tile margin. */
    private band(edge: OpeningEdge, depth: number, color: number, alpha: number): void {
        const dx = edge.side === "east" ? 1 : edge.side === "west" ? -1 : 0
        const dy = edge.side === "south" ? 1 : edge.side === "north" ? -1 : 0
        const rects = drawnFloorRects(edge.x + dx, edge.y + dy, this.floorAt)
        const face = edge.side === "north" ? Math.max(...rects.map(rect => rect.y1))
            : edge.side === "south" ? Math.min(...rects.map(rect => rect.y0))
            : edge.side === "east" ? Math.min(...rects.map(rect => rect.x0))
            : Math.max(...rects.map(rect => rect.x1))
        this.beginFill(color, alpha)
        for (const rect of rects) {
            const width = rect.x1 - rect.x0, height = rect.y1 - rect.y0
            if (edge.side === "north" && rect.y1 === face) {
                const inset = Math.min(depth, height)
                this.drawRect(rect.x0, rect.y1 - inset, width, inset)
            } else if (edge.side === "south" && rect.y0 === face) {
                this.drawRect(rect.x0, rect.y0, width, Math.min(depth, height))
            } else if (edge.side === "east" && rect.x0 === face) {
                this.drawRect(rect.x0, rect.y0, Math.min(depth, width), height)
            } else if (edge.side === "west" && rect.x1 === face) {
                const inset = Math.min(depth, width)
                this.drawRect(rect.x1 - inset, rect.y0, inset, height)
            }
        }
        this.endFill()
    }
}
